import React from 'react';

import MuiBox from '@new-components/atoms/MuiBox';
import Image from '@new-components/atoms/Image';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiLink from '@new-components/atoms/MuiLink';
import DateCard from '../DateCard/DateCard';

import {MediaQuery} from '@constants/theme';

import {LocationIcon} from '@components/SVGIcon/Icons/Location';
import {Calendar} from '@components/SVGIcon/Icons/Calendar';

import {UpcomingEventsTypes} from '../CommunityUpcomingEvents/types';

/**
 *
 * @param {UpcomingEventsTypes} props
 * @prop {string} eventTitle - Title of the event.
 * @prop {{src: string, alt: string}} image - Object containing the source and alt text of the background image.
 * @prop {string} date - Date of the event.
 * @prop {string} month - Month of the event.
 * @prop {string} timestamp - A string that has both the date and month of the event
 * @prop {string} location - Location of the event.
 *
 */

const UpcomingEventsSlide: React.FC<UpcomingEventsTypes> = (props) => {
  return (
    <MuiLink
      href='#'
      target='_blank'
      // Stops the navigation
      onClick={(e) => {
        e.preventDefault();
      }}
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        marginRight: 3,
        display: 'flex',
        flexDirection: 'column',
        rowGap: 2,
        [MediaQuery.desktop]: {
          marginRight: 0,
          transition: 'width .5s ease-in-out',
          width: '25%',
        },
      }}>
      <MuiBox
        sx={{
          height: '365px',
          width: '100%',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
        }}>
        <MuiBox
          sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
          }}>
          <Image
            src={props.image.src}
            alt={props.image.alt}
            fill
            style={{objectFit: 'cover', zIndex: 1}}
          />
          <MuiBox
            sx={{
              background:
                ' radial-gradient(65.13% 66.62% at 50.14% 0%, #000000 0%, rgba(46, 46, 46, 0) 96.86%)',
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              opacity: 0.4,
              transform: 'rotate(180deg)',
            }}
          />
          <MuiBox
            sx={{
              width: '100%',
              position: 'absolute',
              bottom: '0',
              zIndex: '1',
              // To have black-to-transparent gradient that fades upward from the bottom of the tile
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
            }}>
            <DateCard
              date={props.date}
              month={props.month}
              key={props.date + props.eventTitle}
            />
          </MuiBox>
        </MuiBox>
      </MuiBox>

      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          transition: 'opacity .5s ease-in-out',
        }}>
        <MuiTypography
          title={props.eventTitle}
          sx={{
            fontSize: '17px',
            fontWeight: 500,
            lineHeight: '23.8px',
            letterSpacing: '-0.4000000059604645px',
            // Clamp content to 1 lines
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            title: props.eventTitle,
            [MediaQuery.desktop]: {
              fontWeight: 400,
              fontSize: '22px',
              lineHeight: '27.5px',
            },
          }}>
          {props.eventTitle}
        </MuiTypography>
      </MuiBox>

      <MuiBox>
        {/* Details Section */}
        <MuiBox>
          <MuiBox sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Calendar />
            <MuiTypography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'default',
                // Clamp content to 1 lines
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}>
              {props.timestamp}
            </MuiTypography>
          </MuiBox>

          <MuiBox sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <LocationIcon />
            <MuiTypography
              title={props.location}
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '23.8px',
                letterSpacing: '-0.4000000059604645px',
                // Clamp content to 1 lines
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}>
              {props.location}
            </MuiTypography>
          </MuiBox>
        </MuiBox>
      </MuiBox>
    </MuiLink>
  );
};

export default UpcomingEventsSlide;
